import * as React from "react";
import { DropDownList, ComboBox } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import {
  Grid,
  GridColumn as Column,
  GridToolbar,
  GridNoRecords,
} from "@progress/kendo-react-grid";
import { formatDate } from "@progress/kendo-intl";
import { process } from "@progress/kendo-data-query";

import LoadingIndicator from "../../components/LoadingIndicator";
import ErrorBanner from "../../components/ErrorBanner";
import SuccessBanner from "../../components/SuccessBanner";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { useQueryGetOEPeriodList } from "../../data/services/openEnrollmentService";
import {
  useQueryGetClientAetnaMappingList,
  useMutatePostClientAetnaMappingRemove,
  useMutatePostClientAetnaMappingCopy,
} from "../../data/services/clientAetnaPlanMappingService";
import ClientAetnaPlanMappingsImportModal from "./ClientAetnaPlanMappingsImportModal";

// Blank alternative plans render as an em dash rather than an empty cell, so a
// mapping with one or two alternatives does not look like a rendering failure.
const EM_DASH = "—";

const alternativePlanCell = (field) => (props) => (
  <td>{props.dataItem[field] || EM_DASH}</td>
);

const plan1Cell = alternativePlanCell("plan1Id");
const plan2Cell = alternativePlanCell("plan2Id");
const plan3Cell = alternativePlanCell("plan3Id");

// new Date(null) is the epoch and new Date(undefined) is Invalid Date, so an
// absent value would otherwise render as 01/01/1970 or "Invalid Date".
const dateCreatedCell = (props) => {
  const raw = props.dataItem.dateCreated;
  const parsed = raw ? new Date(raw) : null;
  const isValid = parsed !== null && !Number.isNaN(parsed.getTime());

  return <td>{isValid ? formatDate(parsed, "MM/dd/yyyy") : ""}</td>;
};

/**
 * Client Aetna Plan Mappings admin page.
 *
 * Maintains, per client per OE period, the mapping of one Aetna medical plan
 * to up to three competing plans. Import (Task 3) opens
 * ClientAetnaPlanMappingsImportModal; copy-from-season (Task 4) opens a
 * small confirmation dialog that lets an admin carry a prior season's
 * mappings forward instead of re-uploading a CSV every renewal.
 */
const ClientAetnaPlanMappings = () => {
  const [selectedOEPeriod, setSelectedOEPeriod] = React.useState(null);
  const [clientIdFilter, setClientIdFilter] = React.useState("");
  const [rowPendingRemoval, setRowPendingRemoval] = React.useState(null);
  const [showImportModal, setShowImportModal] = React.useState(false);
  const [importResult, setImportResult] = React.useState(null);
  const [showCopyDialog, setShowCopyDialog] = React.useState(false);
  const [copySourcePeriod, setCopySourcePeriod] = React.useState(null);
  const [copyResult, setCopyResult] = React.useState(null);
  const [gridDataState, setGridDataState] = React.useState({
    skip: 0,
    take: 20,
  });

  const { isLoading: oePeriodsLoading, data: oePeriods } =
    useQueryGetOEPeriodList();

  const oePeriodId = selectedOEPeriod?.oePeriodId;

  const { isLoading: listLoading, data: listResponse } =
    useQueryGetClientAetnaMappingList(oePeriodId, clientIdFilter || undefined);

  const removeHandler = useMutatePostClientAetnaMappingRemove();
  const copyHandler = useMutatePostClientAetnaMappingCopy();

  const rows = listResponse?.data ?? [];

  // Copying a season onto itself would retire every row and re-insert it,
  // churning the audit trail for no change. The server rejects it, so the
  // source list never offers the season currently selected on the screen.
  const copySourceOptions = (oePeriods?.data ?? []).filter(
    (period) => period.oePeriodId !== oePeriodId
  );

  const openCopyDialog = () => {
    setCopySourcePeriod(null);
    setShowCopyDialog(true);
  };

  const closeCopyDialog = () => {
    setShowCopyDialog(false);
    setCopySourcePeriod(null);
  };

  const handleCopy = async () => {
    if (!copySourcePeriod) {
      return;
    }

    try {
      const response = await copyHandler.mutateAsync({
        sourceOEPeriodId: copySourcePeriod.oePeriodId,
        targetOEPeriodId: oePeriodId,
      });
      setCopyResult(response?.data);
      closeCopyDialog();
    } catch {
      // Surfaced by <ErrorBanner handler={copyHandler} />.
    }
  };

  const handleRemove = async () => {
    const dataItem = rowPendingRemoval;
    setRowPendingRemoval(null);

    if (!dataItem) {
      return;
    }

    try {
      await removeHandler.mutateAsync({
        clientAetnaPlanMappingId: dataItem.clientAetnaPlanMappingId,
      });
    } catch {
      // Surfaced by <ErrorBanner handler={removeHandler} />.
    }
  };

  const actionCell = (props) => (
    <td>
      <Button
        themeColor={"error"}
        fillMode={"flat"}
        onClick={() => setRowPendingRemoval(props.dataItem)}
      >
        {"Delete"}
      </Button>
    </td>
  );

  if (oePeriodsLoading) {
    return <LoadingIndicator />;
  }

  return (
    <div style={{ padding: "20px" }}>
      <h2>Client Aetna Plan Mappings</h2>
      <p>
        Maps one Aetna medical plan to up to three competing plans, per
        client, for the selected open enrollment season.
      </p>

      <ErrorBanner handler={removeHandler} />
      <SuccessBanner
        handler={removeHandler}
        message={"Mapping removed."}
      />
      {/* No page-level useMutatePostClientAetnaMappingImport() handler: the
          import mutation lives inside the modal, which unmounts on success,
          so there is no live handler left here to hand SuccessBanner. This
          synthetic object satisfies SuccessBanner's entire interface
          ({ isSuccess, reset }) using the result the modal handed back. */}
      <SuccessBanner
        handler={{
          isSuccess: importResult !== null,
          reset: () => setImportResult(null),
        }}
        message={
          importResult
            ? `Import complete: ${importResult.clientsReplaced} client(s) replaced, ` +
              `${importResult.rowsInserted} row(s) inserted, ${importResult.rowsRetired} row(s) retired.`
            : ""
        }
      />

      {/* Same synthetic-handler pattern as importResult above: the copy
          dialog unmounts on success, so there is no live handler left here
          except the counts the mutation resolved with. */}
      <SuccessBanner
        handler={{
          isSuccess: copyResult !== null,
          reset: () => setCopyResult(null),
        }}
        message={
          copyResult
            ? `${copyResult.rowsInserted} mappings copied across ${copyResult.clientsReplaced} clients ` +
              `(${copyResult.rowsRetired} prior row(s) retired).`
            : ""
        }
      />

      <div style={{ display: "flex", gap: "20px", alignItems: "flex-end" }}>
        <div>
          <label
            id={"client-aetna-mapping-oeperiod-label"}
            style={{ display: "block", fontWeight: 500 }}
          >
            OE Period
          </label>
          <DropDownList
            data={oePeriods?.data ?? []}
            ariaLabelledBy={"client-aetna-mapping-oeperiod-label"}
            textField={"oePeriodName"}
            dataItemKey={"oePeriodId"}
            value={selectedOEPeriod}
            onChange={(e) => setSelectedOEPeriod(e.target.value)}
            style={{ width: "260px" }}
          />
        </div>
        <div>
          <label
            id={"client-aetna-mapping-clientid-label"}
            style={{ display: "block", fontWeight: 500 }}
          >
            Client
          </label>
          <ComboBox
            data={[]}
            value={clientIdFilter}
            allowCustom={true}
            ariaLabelledBy={"client-aetna-mapping-clientid-label"}
            placeholder={"Filter by client id"}
            onChange={(e) => setClientIdFilter(e.target.value ?? "")}
            style={{ width: "220px" }}
          />
        </div>
      </div>

      <div style={{ marginTop: "20px" }}>
        {!oePeriodId ? (
          <p>Select an open enrollment season to view its mappings.</p>
        ) : listLoading ? (
          <LoadingIndicator />
        ) : (
          <Grid
            data={process(rows, gridDataState)}
            {...gridDataState}
            onDataStateChange={(event) => setGridDataState(event.dataState)}
            sortable={true}
            filterable={true}
            pageable={{ pageSizes: [20, 50, 100] }}
          >
            <GridToolbar>
              <Button onClick={() => setShowImportModal(true)}>
                {"Import"}
              </Button>
              <Button onClick={openCopyDialog}>{"Copy from season"}</Button>
            </GridToolbar>
            <GridNoRecords>
              No plan mappings are loaded for this season.
            </GridNoRecords>
            <Column field="clientId" title="Client" width={120} />
            <Column field="clientName" title="Client Name" />
            <Column field="aetnaPlanId" title="Aetna Plan" width={140} />
            <Column field="aetnaPlanName" title="Aetna Plan Name" />
            <Column
              field="plan1Id"
              title="Plan 1"
              width={120}
              cell={plan1Cell}
            />
            <Column
              field="plan2Id"
              title="Plan 2"
              width={120}
              cell={plan2Cell}
            />
            <Column
              field="plan3Id"
              title="Plan 3"
              width={120}
              cell={plan3Cell}
            />
            <Column
              field="dateCreated"
              title="Created"
              width={120}
              cell={dateCreatedCell}
            />
            <Column title="Actions" width={120} cell={actionCell} />
          </Grid>
        )}
      </div>

      {rowPendingRemoval && (
        <ConfirmationDialog
          title={"Remove this mapping?"}
          message={
            "This removes the plan mapping for client " +
            rowPendingRemoval.clientId +
            ". Continue?"
          }
          onConfirm={handleRemove}
          onCancel={() => setRowPendingRemoval(null)}
        />
      )}

      {showImportModal && (
        <ClientAetnaPlanMappingsImportModal
          oePeriodId={oePeriodId}
          onClose={() => setShowImportModal(false)}
          onImported={(result) => {
            setShowImportModal(false);
            setImportResult(result);
          }}
        />
      )}

      {showCopyDialog && (
        <Dialog title={"Copy from season"} onClose={closeCopyDialog}>
          <ErrorBanner handler={copyHandler} />
          <p>
            Copying <b>replaces</b>{" "}
            {selectedOEPeriod?.oePeriodName ?? "this season"}&apos;s mappings
            for every client{" "}
            {copySourcePeriod?.oePeriodName ?? "the source season"} has a
            mapping for. Clients{" "}
            {copySourcePeriod?.oePeriodName ?? "the source season"} does not
            mention are left untouched. This cannot be undone except by
            another copy or a re-import.
          </p>
          <div>
            <label
              id={"copy-source-period-label"}
              style={{ display: "block", fontWeight: 500 }}
            >
              Copy mappings from
            </label>
            <DropDownList
              data={copySourceOptions}
              ariaLabelledBy={"copy-source-period-label"}
              textField={"oePeriodName"}
              dataItemKey={"oePeriodId"}
              value={copySourcePeriod}
              onChange={(e) => setCopySourcePeriod(e.target.value)}
              style={{ width: "260px" }}
            />
          </div>
          <DialogActionsBar>
            <Button onClick={closeCopyDialog}>{"Cancel"}</Button>
            <Button
              type={"submit"}
              themeColor={"primary"}
              disabled={!copySourcePeriod || copyHandler.isLoading}
              onClick={handleCopy}
            >
              {"Copy"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default ClientAetnaPlanMappings;
